import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { COURSES_URL, ENROLLMENT_URL } from '../../Services/courseService';
import { Link, useNavigate } from 'react-router-dom';
import './ExploringSkills.css';
import { Splide, SplideSlide } from '@splidejs/react-splide';
import '@splidejs/react-splide/css';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import '@fortawesome/fontawesome-free/css/all.min.css';
import ClipLoader from 'react-spinners/ClipLoader';
import { addToCart } from '../../Services/courseService';
import { addToReduxCart } from '../../ReduxStore/CartSlice';
import { toast } from 'react-toastify';

const CourseTemplate = ({ coursesData, pageTitle, error }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [hoveredCourseIndex, setHoveredCourseIndex] = useState(null);
  const [btnClicked, setBtnClicked] = useState({});
  const [enrollmentStatus, setEnrollmentStatus] = useState({});
  const [loading, setLoading] = useState(false);
  const userId = localStorage.getItem('userId');
  const [courseRatings, setCourseRatings] = useState({});
  const courseRequestInProgress = useRef({});
  const splideRef = useRef();

  const getRemainingSlots = (course) => {
    if (!course?.total_slots) return 0;
    const total = Number(course.total_slots);
    const filled = Number(course.filled_slots);
    const remaining = total - filled;
    return remaining > 0 ? remaining : 0;
  };


  const handleCardMouseEnter = async (index, courseId) => {
    setHoveredCourseIndex(index);
    const isEnrolled = await handleSideCardHover(courseId);
    setEnrollmentStatus(prev => ({ ...prev, [courseId]: isEnrolled }));
    updateSideCardPosition();
  };

  const getVisibleSlidesCount = () => {
    const width = window.innerWidth;
    if (width > 1356) return 5;
    if (width > 1086) return 4;
    if (width >= 768) return 3;
    if (width >= 480) return 2;
    return 2;
  };

  const updateSideCardPosition = useCallback(() => {
    const slides = splideRef.current?.splide?.root.querySelectorAll('.splide__slide');
    const visibleSlidesCount = getVisibleSlidesCount();

    setTimeout(() => {
      const lastVisibleIndex = Math.min(visibleSlidesCount - 1, slides.length - 1);

      slides.forEach((slide, index) => {
        const sideCard = document.querySelector(`.side-card-${index}`);
        if (sideCard) {
          if (index === lastVisibleIndex || index === slides.length - 1) {
            sideCard.style.left = 'auto';
            sideCard.style.right = '100%';
          } else if ((index + 1) % visibleSlidesCount === 0) {
            sideCard.style.left = 'auto';
            sideCard.style.right = '100%';
          } else {
            sideCard.style.left = '100%';
            sideCard.style.right = 'auto';
          }
        }
      });
    }, 0);
  }, []);

  useLayoutEffect(() => {
    if (hoveredCourseIndex !== null) {
      updateSideCardPosition();
    }
  }, [hoveredCourseIndex, updateSideCardPosition]);

  const checkEnrollmentStatus = async (userId, courseId) => {
    try {
      const enrollmentRes = await axios.get(`${ENROLLMENT_URL}/${userId}/${courseId}/getEnrollmentStatus`);
      setEnrollmentStatus(prev => ({ ...prev, [courseId]: enrollmentRes?.data }));
      return enrollmentRes?.data;
    } catch (err) {
      if (err?.response?.status === 400) {
        setEnrollmentStatus(prev => ({ ...prev, [courseId]: false }));
        return false;
      } else {
        console.error("Unexpected error checking enrollment status:", err);
        toast.error("Error checking enrollment status");
        return false;
      }
    }
  };

  const handleSideCardHover = async (courseId) => {
    if (userId) {
      const isEnrolled = await checkEnrollmentStatus(userId, courseId);
      return isEnrolled;
    }
    return false;
  };

  const generateSlug = (title) =>
    title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const handleCardClick = async (course) => {
    const slug = course.slug || generateSlug(course?.title);

    if (userId) {
      const isEnrolled = await checkEnrollmentStatus(userId, course.courseId);
      if (isEnrolled) {
        navigate(`/enrolledCourse/${course.courseId}`);
        return;
      }
    }

    const slugCourseMap = JSON.parse(localStorage.getItem('slugCourseMap')) || {};
    slugCourseMap[slug] = course.courseId;
    localStorage.setItem('slugCourseMap', JSON.stringify(slugCourseMap));

    navigate(`/courses/${slug}`);
  };

  const handleAddToCart = async (course) => {
    toast.dismiss();

    if (!userId) {
      toast.error('Please login to add items to cart');
      return;
    }

    if (course.price >= 9999 && getRemainingSlots(course) <= 0) {
      toast.error('No slots remaining for this course');
      return;
    }

    try {
      setLoading(true);
      const res = await addToCart(userId, course?.courseId);

      if (res.status === 201) {
        dispatch(addToReduxCart({
          ...course,
          price: Math.floor(course?.price),
          quantity: 1
        }));
        setBtnClicked(prev => ({ ...prev, [course?.courseId]: true }));
        toast.success('Item added to cart!');
      } else if (res.status === 500) {
        toast.error("This item is already in your cart");
      } else {
        toast.error(res?.data?.message);
      }
    } catch (error) {
      toast.dismiss();
      toast.error('This item is already in your cart');
      setBtnClicked(prev => ({ ...prev, [course?.courseId]: true }));
    } finally {
      setLoading(false);
    }
  };

  const renderRating = (rating) => {
    const fullStars = Math.floor(rating);
    const halfStar = rating % 1 >= 0.5;
    let stars = '★'.repeat(fullStars);
    if (halfStar) stars += '☆';
    return stars;
  };

  const getAverageRatingByCourse = useCallback(async (courseId) => {
    if (courseRequestInProgress.current[courseId]) return;

    courseRequestInProgress.current[courseId] = true;
    try {
      const response = await axios.get(`${COURSES_URL}/course/${courseId}/average-rating`);
      setCourseRatings(prevRatings => ({
        ...prevRatings,
        [courseId]: response.data.data
      }));
    } catch (error) {
      console.error('Error fetching rating:', error);
      setCourseRatings(prevRatings => ({
        ...prevRatings,
        [courseId]: 4
      }));
    }
  }, []);

  useEffect(() => {
    coursesData.forEach((course) => {
      if (courseRatings[course.courseId] === undefined) {
        getAverageRatingByCourse(course.courseId);
      }
    });
  }, [coursesData, courseRatings, getAverageRatingByCourse]);

  if (error) return <div className='loading-courses'>{error}</div>;
  if (coursesData.length === 0) return null;

  return (
    <div className='ES-Container'>
      <>
        <h2 className="ES-Heading">{pageTitle}</h2>

        <Splide
          options={{
            perPage: getVisibleSlidesCount(),
            gap: '0rem',
            pagination: false,
            arrows: true,
            loop: true,
            drag: false,
            fixedWidth: '270px',
            breakpoints: {
              1086: { perPage: 3 },
              768: { perPage: 2, fixedWidth: '245px' },
              480: { gap: '0.7rem', fixedWidth: '165px' }
            },
          }}
          ref={splideRef}
          className='splide-course-container'
        >
          {coursesData?.map((course, index) => (
            <SplideSlide key={course?.courseId}>
              <div
                className='es-course-maincontainer'
                onMouseEnter={() => handleCardMouseEnter(index, course?.courseId)}
                onMouseLeave={() => setHoveredCourseIndex(null)}
              >
                <div className="es-course-card-container" onClick={() => handleCardClick(course)}>
                  <div className="es-course-card">
                    <img
                      src={course?.imageUrl}
                      alt={course?.title}
                      className="es-course-image"
                    />
                    <div className='es-description-container'>
                      <h2 className="es-course-title">{course?.title}</h2>
                      <p className="es-sidecard-description">{course?.description}</p>
                      <div className="course-rating">
                        {courseRatings[course.courseId] || 4}
                        {renderRating(courseRatings[course.courseId] || 4)}
                      </div>
                      <div className="course-footer">
                        {/* <p className="course-instructor">{course?.createdBy}</p> */}
                        <p className="course-instructor">
                          {window.innerWidth <= 480
                            ? course?.createdBy?.split(" ").slice(0, 2).join(" ")
                            : course?.createdBy}
                        </p>
                        <p className="course-price">₹{Math.floor(course?.price)}</p>
                      </div>
                    </div>
                  </div>

                  <div className="es-course-card-absolute">
                    <img
                      src={course?.imageUrl}
                      alt={course?.title}
                      className="es-course-image"
                      style={{
                        objectFit: "cover",
                      }}
                    />
                    <div className='es-description-container'>
                      <h2 className="es-course-title">{course?.title}</h2>
                      <p className="es-sidecard-description show">{course?.description}</p>
                      <div className="course-rating">
                        {courseRatings[course.courseId] || 4}
                        {renderRating(courseRatings[course.courseId] || 4)}
                      </div>
                      <div>
                        {course.price >= 9999 && Number(course.total_slots) > 0 && getRemainingSlots(course) > 0 && (
                          <p
                            className="es-sidecard-slots"
                            style={{
                              position: "absolute",
                              top: "-15px",
                              left: "-1px",
                              background: "rgba(223, 32, 32, 1)",
                              color: "white",
                              padding: "4px 4px 4px 4px",
                              fontSize: "13px",
                              height: "15px",
                              fontWeight: "bold",
                              borderRadius: "5px",
                            }}
                          >
                            Remaining Slots: {getRemainingSlots(course)}
                          </p>
                        )}

                      </div>
                      <div className="course-footer">
                        {/* <p className="course-instructor">{course?.createdBy}</p> */}
                        {/* <p
                          className="course-instructor"
                          data-shortname={
                            course?.createdBy
                              ?.split(" ")
                              ?.slice(0, 2)
                              ?.join(" ") + "..."
                          }
                        >
                          {course?.createdBy}
                        </p> */}
                        <p className="course-instructor">
                          {window.innerWidth <= 480
                            ? course?.createdBy?.split(" ").slice(0, 2).join(" ")
                            : course?.createdBy}
                        </p>


                        <p className="course-price">₹{Math.floor(course?.price)}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {hoveredCourseIndex === index && (
                  <div className={`side-card side-card-${index}`} key={`side-card-${course?.courseId}`}>
                    <h3 className="es-sidecard-title">{course?.title}</h3>
                    <p className="es-sidecard-description">{course?.description}</p>
                    <p className="es-sidecard-additionalInfo">{course?.additionalInfo}</p>

                    <div className='sidecard-buttons-container'>
                      {enrollmentStatus[course?.courseId] ? (
                        <button className="ES-button" onClick={() => navigate(`/enrolledCourse/${course?.courseId}`)}>Continue</button>
                      ) : (
                        <span>
                          {!btnClicked[course?.courseId] ? (
                            <button className="ES-button" onClick={() => handleAddToCart(course)}>
                              {loading ? <ClipLoader color="white" loading={loading} size={17} /> : "Add to Cart"}
                            </button>
                          ) : (
                            <Link to='/Cart'>
                              <button className="ES-button">Go to Cart</button>
                            </Link>
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </SplideSlide>
          ))}
        </Splide>
      </>
    </div>
  );
};

export default CourseTemplate;
